import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Stack, Button, CircularProgress } from '@mui/material';
import { Add as AddIcon, Person as PersonIcon } from '@mui/icons-material';
import CharacterCard from '../../../../../features/characters/CharacterCard';
import useThemeStyles from '../../../../../hooks/useThemeStyles';
import useProjectCharacters from '../hooks/useProjectCharacters';

const CharactersModeBody = ({ projectId, handleAddCharacter, onCharacterClick }) => {
  const { colors } = useThemeStyles();
  const { characters, families, loading } = useProjectCharacters(projectId);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.sidebar.background,
        minWidth: 0,
      }}
    >
      {handleAddCharacter && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border.subtle}` }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCharacter}
            fullWidth
            sx={{
              textTransform: 'none',
              color: colors.text.primary,
              borderColor: colors.border.subtle,
              '&:hover': {
                backgroundColor: colors.sidebar.headerHover,
                borderColor: colors.interactive.primary,
              },
            }}
          >
            Add Character
          </Button>
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} sx={{ color: colors.interactive.primary }} />
          </Box>
        )}
        {!loading && characters.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              color: colors.text.secondary,
              textAlign: 'center',
            }}
          >
            <PersonIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              No characters yet
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Click &quot;Add Character&quot; to create your first character
            </Typography>
          </Box>
        )}
        {!loading && characters.length > 0 && (
          <Stack spacing={1.5}>
            {characters.map((character) => {
              const family = character.familyId
                ? families.find((f) => f.id === character.familyId)
                : null;

              return (
                <CharacterCard
                  key={character.id || character._id}
                  character={character}
                  family={family}
                  onClick={() => onCharacterClick && onCharacterClick(character)}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

CharactersModeBody.propTypes = {
  projectId: PropTypes.string,
  handleAddCharacter: PropTypes.func,
  onCharacterClick: PropTypes.func,
};

CharactersModeBody.defaultProps = {
  projectId: null,
  handleAddCharacter: null,
  onCharacterClick: null,
};

export default CharactersModeBody;
